/**
 * GhostProtocolSkillEffect — apotheosis_astral_projection (Astral oracle-path apotheosis).
 *
 * Total dissolution — not projection, not separation. The player *becomes* information
 * with intent. For 6 seconds the body is unhittable, invisible to idle enemies, and
 * passes through walls. Every enemy the ghost moves through is marked without limit.
 *
 * On return:
 *   1. All marked enemies receive a 500ms stagger (attack interruption).
 *   2. The player enters Oracle/Prescience state for free — no cooldown consumed,
 *      no reliability check. This is the capstone privilege of the oracle path.
 *
 * Phase sequence: toggle ghost window (6000ms) → very_long cooldown (30000ms).
 * Second activation during the window triggers immediate return.
 *
 * OR-gate unlock: SkillProgressionComponent checks
 *   `parentIds.some(id => unlockedNodes.has(id))` for this node's parentIds
 *   array (`["astral_projection_passive", "temporal_echo_passive"]`). Ghost Protocol
 *   is reachable via either the Astral Projection sub-path or the Temporal Echo sub-path.
 *
 * Attunement cost: 1 slot (all active skills, apotheosis included, cost exactly 1
 * slot — design-decisions.md §1. The JSON description says "2 slots" but that text
 * is incorrect per the V4 design doc; do not implement 2-slot cost.)
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § apotheosis_astral_projection
 * @see design-decisions.md § Apotheosis Gate Semantics
 * @see design-decisions.md § Ghost Protocol Free Prescience: Bypasses Cooldown
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

/**
 * Adapter for ghost-form entity management.
 *
 * In real gameplay, dematerialize() disables the physics body for damage
 * collisions, hides the player from idle enemy LOS checks, and disables
 * static geometry collision (wall pass-through). rematerialize() reverses
 * all three. This adapter keeps the skill logic free of Phaser APIs.
 */
export interface GhostFormAdapter {
  /** Disable damage collision, hide from idle enemy LOS, disable wall collision. */
  dematerialize(): void;
  /** Restore collision, LOS visibility, and wall collision. */
  rematerialize(): void;
}

export interface GhostProtocolDeps {
  /** Ghost-form entity adapter (handles physics/LOS changes). */
  readonly ghostForm: GhostFormAdapter;
  /**
   * Status effect application — applies stagger to marked enemies on return.
   * The mock records all calls for simulation validation.
   */
  readonly statusEffects: {
    apply(effectId: string, targetId: string, durationMs: number): void;
  };
  /**
   * The active Oracle-family skill (Prescience or Oracle with free-activation wrapper).
   * Null if neither Oracle nor Prescience is unlocked — the free activation is dormant.
   *
   * In practice, reaching Ghost Protocol requires deep oracle-path progression,
   * so this is almost always Prescience. The factory wiring task handles the
   * null case gracefully.
   */
  readonly oracleSkill: { activateForFree(): boolean } | null;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface GhostProtocolConfig {
  /** Duration of the ghost window in ms (6000ms). */
  readonly ghostDurationMs: number;
  /** Very long cooldown tier in ms (30000ms). */
  readonly cooldownMs: number;
  /** Duration of the stagger applied to marked enemies on return in ms (500ms). */
  readonly staggerDurationMs: number;
}

const DEFAULT_CONFIG: GhostProtocolConfig = {
  ghostDurationMs: 6000,
  cooldownMs: 30000,    // very_long apotheosis tier
  staggerDurationMs: 500,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class GhostProtocolSkillEffect extends ToggleSkillEffect {
  readonly skillId = 'apotheosis_astral_projection';

  private readonly deps: GhostProtocolDeps;
  readonly config: GhostProtocolConfig;

  /** Countdown for the current ghost window. */
  private _countdownMs = 0;

  /** Enemies marked during the current ghost phase (unlimited). */
  private _markedEnemyIds = new Set<string>();

  /** Total activations since construction (for simulation stats). */
  private _activationsCount = 0;

  /**
   * Enemy IDs marked in the most recent completed ghost phase.
   * Populated by onDeactivate(); cleared on next activation.
   */
  private _lastMarkedEnemyIds: ReadonlySet<string> = new Set();

  /** Whether the most recent return triggered a free oracle activation. */
  private _lastFreeOracleResult: boolean | null = null;

  constructor(deps: GhostProtocolDeps, config: Partial<GhostProtocolConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get countdownMs(): number { return this._countdownMs; }
  get markedEnemyIds(): ReadonlySet<string> { return this._markedEnemyIds; }
  get activationsCount(): number { return this._activationsCount; }
  get lastMarkedEnemyIds(): ReadonlySet<string> { return this._lastMarkedEnemyIds; }
  get lastFreeOracleResult(): boolean | null { return this._lastFreeOracleResult; }

  // ---------------------------------------------------------------------------
  // ToggleSkillEffect implementation
  // ---------------------------------------------------------------------------

  canActivate(): boolean {
    return this.cooldownRemaining <= 0 && !this._isActive;
  }

  /**
   * Activate ghost form, or — if already active — trigger early return.
   *
   * First call: enter ghost state (full dematerialization).
   * Second call during active window: immediate return (deactivate), starts cooldown.
   */
  activate(): boolean {
    if (this._isActive) {
      // Manual return — reactivating ends the ghost state immediately.
      this.deactivate();
      return true;
    }

    if (!this.canActivate()) return false;

    this._markedEnemyIds = new Set();
    this._lastMarkedEnemyIds = new Set();
    this._lastFreeOracleResult = null;
    this._countdownMs = this.config.ghostDurationMs;
    this._isActive = true;
    this._activationsCount++;

    // Enter ghost form: disable collision, hide from LOS, enable wall pass-through.
    this.deps.ghostForm.dematerialize();

    return true;
  }

  /**
   * Mark an enemy during the ghost phase.
   * Called externally when the ghost form overlaps an enemy.
   * No mark cap — Ghost Protocol marks every enemy it touches.
   */
  markEnemy(enemyId: string): void {
    if (!this._isActive) return;
    this._markedEnemyIds.add(enemyId);
  }

  protected override updateWhileActive(delta: number): void {
    this._countdownMs = Math.max(0, this._countdownMs - delta);
    if (this._countdownMs <= 0) {
      this.deactivate();
    }
  }

  protected override onDeactivate(): void {
    // Rematerialize: restore collision, LOS, wall collision.
    this.deps.ghostForm.rematerialize();

    // Snapshot marked enemies before clearing.
    this._lastMarkedEnemyIds = new Set(this._markedEnemyIds);

    // Apply stagger to every marked enemy — attack interruption for 500ms.
    // Per ADR-065: stagger has a 500ms immunity window post-expiry, so enemies
    // recently staggered may be immune. The StatusEffectSystem handles immunity
    // checks internally; we apply unconditionally and let it filter.
    for (const enemyId of this._markedEnemyIds) {
      this.deps.statusEffects.apply(
        'stagger',
        enemyId,
        this.config.staggerDurationMs,
      );
    }

    // Free Oracle/Prescience activation — the capstone privilege of the oracle path.
    // Fires regardless of whether Oracle/Prescience is on cooldown.
    if (this.deps.oracleSkill !== null) {
      this._lastFreeOracleResult = this.deps.oracleSkill.activateForFree();
    } else {
      this._lastFreeOracleResult = null;
    }

    // Clear marks for next activation.
    this._markedEnemyIds = new Set();

    // Start very long cooldown.
    this.cooldownRemaining = this.config.cooldownMs;
  }

  override destroy(): void {
    if (this._isActive) {
      this.deps.ghostForm.rematerialize();
    }
    super.destroy();
  }
}
